import { memo } from 'react'
import {
  endTangent,
  INK,
  lineGeom,
  linePath,
  paintOrder,
  pointAt,
  STICKY_STEPS,
  type Cap,
  type El,
  type LineGeom,
  type Pt,
  type ShapeKind,
} from './model'
import { hopsFor } from './lines'
import { BOARD_FONT, blockHeight, LINE_HEIGHT, MONO_FONT, textWidth, wrap } from './text'
import { noteLayout, type NoteLayout } from './noteText'
import { useBlobUrl } from '../ui/useBlobUrl'
import { dynamoAttachmentBytes } from '../chat/projectDynamo'

/**
 * One element of the whiteboard, drawn in board coordinates (the canvas scales the group they stand in). Pure SVG:
 * the same picture on every screen, the iOS app's WebKit included.
 */

/** The outline of a shape `w`×`h` at the origin. */
export function shapePath(kind: ShapeKind, w: number, h: number): string {
  switch (kind) {
    case 'rect':
      return `M0,0 H${w} V${h} H0 Z`
    case 'round': {
      const r = Math.min(16, w / 4, h / 4)
      return roundRect(w, h, r)
    }
    case 'pill':
      return roundRect(w, h, Math.min(h, w) / 2)
    case 'ellipse':
      return `M${w / 2},0 A${w / 2},${h / 2} 0 1 1 ${w / 2},${h} A${w / 2},${h / 2} 0 1 1 ${w / 2},0 Z`
    case 'diamond':
      return `M${w / 2},0 L${w},${h / 2} L${w / 2},${h} L0,${h / 2} Z`
    case 'parallelogram': {
      const s = Math.min(w * 0.2, h * 0.6)
      return `M${s},0 H${w} L${w - s},${h} H0 Z`
    }
    case 'triangle':
      return `M${w / 2},0 L${w},${h} H0 Z`
    case 'hexagon': {
      const s = Math.min(w * 0.22, h * 0.5)
      return `M${s},0 H${w - s} L${w},${h / 2} L${w - s},${h} H${s} L0,${h / 2} Z`
    }
    case 'cylinder': {
      const ry = Math.min(h * 0.14, 18)
      return `M0,${ry} V${h - ry} A${w / 2},${ry} 0 0 0 ${w},${h - ry} V${ry} A${w / 2},${ry} 0 0 0 0,${ry} Z`
    }
    case 'document': {
      const d = Math.min(h * 0.14, 18)
      return `M0,0 H${w} V${h - d} C${w * 0.75},${h - 2.4 * d} ${w * 0.25},${h + 0.6 * d} 0,${h - d} Z`
    }
  }
}

function roundRect(w: number, h: number, r: number): string {
  return `M${r},0 H${w - r} A${r},${r} 0 0 1 ${w},${r} V${h - r} A${r},${r} 0 0 1 ${w - r},${h} H${r} A${r},${r} 0 0 1 0,${h - r} V${r} A${r},${r} 0 0 1 ${r},0 Z`
}

/** The part of a shape text may use — a diamond's middle, under a cylinder's lid. */
export function textBox(el: El): { x: number; y: number; w: number; h: number } {
  const pad = el.t === 'sticky' ? 16 : el.t === 'text' ? 0 : 10
  let { w, h } = el
  let x = 0
  let y = 0
  switch (el.t === 'shape' ? el.shape : undefined) {
    case 'diamond':
      x = w * 0.2
      y = h * 0.2
      w *= 0.6
      h *= 0.6
      break
    case 'triangle':
      x = w * 0.22
      y = h * 0.38
      w *= 0.56
      h *= 0.58
      break
    case 'ellipse':
      x = w * 0.12
      y = h * 0.12
      w *= 0.76
      h *= 0.76
      break
    case 'parallelogram':
    case 'hexagon':
      x = Math.min(el.w * 0.2, el.h * 0.6) * 0.7
      w -= 2 * x
      break
    case 'cylinder': {
      const ry = Math.min(el.h * 0.14, 18)
      y = ry * 2
      h -= ry * 2
      break
    }
  }
  return { x: x + pad, y: y + pad, w: Math.max(4, w - 2 * pad), h: Math.max(4, h - 2 * pad) }
}

/** A note's words start at the board's ordinary size, as a shape's do (owner, 2026-11-02: 便條貼預設字體過大, 讓預設字體與
 *  一般 icon 的字體同等大小就好) — they were fitted from 48 down, so three words filled the paper. */
export const DEFAULT_FS: Record<string, number> = { sticky: 16, shape: 16, text: 20, line: 14, frame: 14 }

/** The text of an element as it is laid out: font size and lines. */
export function layoutText(el: El): { fs: number; lines: string[] } {
  const text = el.text ?? ''
  const box = textBox(el)
  // A note's words never leave its paper (owner, 2026-11-01: 文字內容都會超過便條的邊框): its size — chosen with A+/A−,
  // else the ordinary one — is the largest they may be, and they shrink below it when they do not fit.
  if (el.t === 'sticky') {
    const { fs, lines } = noteLayoutOf(el)
    return { fs, lines }
  }
  const fs = el.fs ?? DEFAULT_FS[el.t] ?? 16
  return { fs, lines: text ? wrap(text, fs, box.w, el.bold) : [] }
}

/**
 * A note's words — Markdown (board/noteText.ts; owner, 2026-11-02: 讓便條貼顯示 Markdown 文本) — laid out on its paper: its size
 * (chosen with A+/A−, else the ordinary one) the largest they may be, smaller steps when they do not fit.
 */
function noteLayoutOf(el: El): NoteLayout {
  const box = textBox(el)
  const top = el.fs ?? DEFAULT_FS['sticky']!
  return noteLayout(el.text ?? '', box.w, box.h, [top, ...STICKY_STEPS.filter((s) => s < top)], { bold: el.bold, align: el.align })
}

/** A note's words as noteText laid them out: rows of styled runs, a list's marks, a to-do's boxes, code's grey, a quote's bar. */
function NoteText({ el }: { el: El }) {
  const box = textBox(el)
  const ink = el.color ?? INK
  const { draws } = noteLayoutOf(el)
  if (!draws.length) return null
  return (
    <g transform={`translate(${box.x},${box.y})`}>
      {draws.map((d, i) => {
        if (d.k === 'rect') return <rect key={i} x={d.x} y={d.y} width={d.w} height={d.h} fill={d.fill} rx={1} />
        if (d.k === 'box') {
          return (
            <g key={i}>
              <rect
                x={d.x}
                y={d.y}
                width={d.size}
                height={d.size}
                rx={d.size * 0.15}
                fill={d.checked ? '#2383e2' : 'none'}
                stroke={d.checked ? '#2383e2' : 'rgba(0,0,0,0.45)'}
                strokeWidth={Math.max(1, d.size * 0.09)}
              />
              {d.checked && (
                <path
                  d={`M${d.x + d.size * 0.22},${d.y + d.size * 0.52} L${d.x + d.size * 0.42},${d.y + d.size * 0.72} L${d.x + d.size * 0.8},${d.y + d.size * 0.3}`}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={Math.max(1.2, d.size * 0.13)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </g>
          )
        }
        return (
          <text key={i} x={d.x} y={d.y} fontSize={d.fs} fontFamily={BOARD_FONT} fill={ink} textAnchor={d.anchor} style={{ whiteSpace: 'pre' }}>
            {d.segs.map((g, k) => (
              <tspan
                key={k}
                {...(g.bold ? { fontWeight: 600 } : {})}
                {...(g.italic ? { fontStyle: 'italic' } : {})}
                {...(g.mono ? { fontFamily: MONO_FONT } : {})}
                {...(g.color ? { fill: g.color } : {})}
                {...(g.strike || g.underline ? { textDecoration: [g.underline ? 'underline' : '', g.strike ? 'line-through' : ''].filter(Boolean).join(' ') } : {})}
              >
                {g.text || ' '}
              </tspan>
            ))}
          </text>
        )
      })}
    </g>
  )
}

/** How tall a text element must stand to show all of its lines. */
export function textHeightFor(el: El): number {
  const { fs, lines } = layoutText(el)
  return Math.max(fs * LINE_HEIGHT, blockHeight(Math.max(1, lines.length), fs))
}

function TextLines({
  lines,
  fs,
  box,
  align,
  bold,
  color,
  valign,
}: {
  lines: string[]
  fs: number
  box: { x: number; y: number; w: number; h: number }
  align: 'left' | 'center' | 'right'
  bold?: boolean | undefined
  color: string
  valign: 'top' | 'middle'
}) {
  if (!lines.length) return null
  const lh = fs * LINE_HEIGHT
  const total = lines.length * lh
  const top = valign === 'middle' ? box.y + (box.h - total) / 2 : box.y
  const x = align === 'left' ? box.x : align === 'right' ? box.x + box.w : box.x + box.w / 2
  const anchor = align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle'
  return (
    <text
      x={x}
      fontSize={fs}
      fontFamily={BOARD_FONT}
      fontWeight={bold ? 600 : 400}
      fill={color}
      textAnchor={anchor}
      style={{ whiteSpace: 'pre' }}
    >
      {lines.map((l, i) => (
        // baseline ~ 0.8 em under the line's top, half the leading above
        <tspan key={i} x={x} y={top + i * lh + (lh - fs) / 2 + fs * 0.82}>
          {l || ' '}
        </tspan>
      ))}
    </text>
  )
}

/** The board is paper: a hollow arrowhead is filled with it, so the line does not show through. */
const PAPER = '#ffffff'

/** How far a line stops short of its end for this cap (so it does not show through a hollow one or poke past a point). */
export function capTrim(cap: Cap, s: number, sw: number): number {
  switch (cap) {
    case 'arrow':
    case 'stealth':
    case 'diamond_f':
      return s * 0.5
    case 'triangle':
    case 'diamond':
      return s - sw / 2
    case 'circle_f':
      return s * 0.38
    case 'circle':
      return s * 0.76 - sw / 2
    case 'open':
      return sw / 2
    default:
      return 0
  }
}

/** The size a line's caps are drawn at, from its width. */
const capSize = (sw: number) => 8 + sw * 2

/**
 * A line's end as Miro draws it (lines.ts says why the set): `at` is the end, `dir` the way the line arrives there.
 * Arrowheads point at the end; the crow's-foot marks of a data diagram stand across the line just before it.
 */
export function CapMark({
  cap,
  at,
  dir,
  s,
  sw,
  color,
  paper = PAPER,
}: {
  cap: Cap
  at: Pt
  dir: Pt
  s: number
  sw: number
  color: string
  /** What a hollow cap is filled with: the board's paper (a toolbar's picture of it: nothing). */
  paper?: string
}) {
  if (cap === 'none') return null
  const n = { x: -dir.y, y: dir.x }
  const P = (back: number, side: number) => `${Math.round((at.x - dir.x * back + n.x * side) * 100) / 100},${Math.round((at.y - dir.y * back + n.y * side) * 100) / 100}`
  const C = (back: number) => ({ cx: at.x - dir.x * back, cy: at.y - dir.y * back })
  const stroke = { stroke: color, strokeWidth: sw, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const }
  const bar = (d: number) => <path key={`b${d}`} d={`M${P(d, s * 0.45)} L${P(d, -s * 0.45)}`} fill="none" {...stroke} />
  const crow = <path key="crow" d={`M${P(0, s * 0.45)} L${P(s * 0.8, 0)} L${P(0, -s * 0.45)}`} fill="none" {...stroke} />
  const ring = (d: number, r: number) => <circle key={`r${d}`} {...C(d + r)} r={r} fill={paper} {...stroke} />
  switch (cap) {
    case 'arrow':
      return <path d={`M${P(0, 0)} L${P(s, s * 0.5)} L${P(s, -s * 0.5)} Z`} fill={color} />
    case 'stealth':
      return <path d={`M${P(0, 0)} L${P(s, s * 0.5)} L${P(s * 0.7, 0)} L${P(s, -s * 0.5)} Z`} fill={color} stroke={color} strokeWidth={Math.min(sw, 1)} strokeLinejoin="round" />
    case 'open':
      return <path d={`M${P(s, s * 0.5)} L${P(0, 0)} L${P(s, -s * 0.5)}`} fill="none" {...stroke} />
    case 'triangle':
      return <path d={`M${P(sw / 2, 0)} L${P(s, s * 0.5)} L${P(s, -s * 0.5)} Z`} fill={paper} {...stroke} />
    case 'diamond_f':
    case 'diamond': {
      const hollow = cap === 'diamond'
      const o = hollow ? sw / 2 : 0
      return <path d={`M${P(o, 0)} L${P(s / 2, s * 0.35)} L${P(s - o, 0)} L${P(s / 2, -s * 0.35)} Z`} fill={hollow ? paper : color} {...(hollow ? stroke : {})} />
    }
    case 'circle_f':
      return <circle {...C(s * 0.38)} r={s * 0.38} fill={color} />
    case 'circle':
      return <circle {...C(s * 0.38)} r={s * 0.38 - sw / 2} fill={paper} {...stroke} />
    case 'erd_one':
      return <g>{bar(s * 0.6)}</g>
    case 'erd_only_one':
      return <g>{[bar(s * 0.45), bar(s * 0.85)]}</g>
    case 'erd_zero_or_one':
      return <g>{[bar(s * 0.45), ring(s * 0.75, s * 0.28)]}</g>
    case 'erd_many':
      return <g>{crow}</g>
    case 'erd_one_or_many':
      return <g>{[crow, bar(s * 1.05)]}</g>
    case 'erd_zero_or_many':
      return <g>{[crow, ring(s * 0.95, s * 0.28)]}</g>
    default:
      return null
  }
}

/** A line's caps, start and end, with their defaults: an arrow at the end only (as a line always started). */
export function capsOf(el: El): { head: Cap; tail: Cap } {
  return { head: el.head ?? 'arrow', tail: el.tail ?? 'none' }
}

/** The stroke pattern of a line: solid, dashed, or dotted (a dot every so often — a round cap on no length). */
export function dashOf(el: El, sw: number): string | undefined {
  if (el.dash && el.dot) return `0.01 ${sw * 2.6}`
  if (el.dash) return `${sw * 4} ${sw * 3}`
  return undefined
}

/**
 * A line as drawn (its geometry is computed by the caller — the canvas needs it for hits too). `editing`: its label is
 * being typed in the editor laid over it, so the label is not drawn twice. `hops`: where it jumps lines under it.
 */
function LineShape({
  el,
  g,
  hidden,
  editing,
  hops,
}: {
  el: El
  g: LineGeom
  hidden?: boolean | undefined
  editing?: boolean | undefined
  hops?: Map<number, Pt[]> | undefined
}) {
  const color = el.stroke && el.stroke !== 'none' ? el.stroke : INK
  const sw = el.sw ?? 2
  const size = capSize(sw)
  const { head, tail } = capsOf(el)
  const d = linePath(g, { trimA: capTrim(tail, size, sw), trimB: capTrim(head, size, sw), hops })
  const label = editing ? '' : (el.text ?? '')
  const m = label ? pointAt(g, el.lt ?? 0.5) : null
  const fs = el.fs ?? DEFAULT_FS['line']!
  const lw = label ? Math.max(...label.split('\n').map((l) => textWidth(l, fs, el.bold))) + 12 : 0
  const lh = label ? label.split('\n').length * fs * LINE_HEIGHT + 6 : 0
  return (
    <g opacity={hidden ? 0.35 : 1} data-line={el.id}>
      <path d={d} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={dashOf(el, sw)} strokeLinejoin="round" strokeLinecap="round" />
      <CapMark cap={head} at={g.b.p} dir={endTangent(g, 'b')} s={size} sw={sw} color={color} />
      <CapMark cap={tail} at={g.a.p} dir={endTangent(g, 'a')} s={size} sw={sw} color={color} />
      {m && (
        <g className="bd-line-label">
          <rect x={m.x - lw / 2} y={m.y - lh / 2} width={lw} height={lh} rx={4} fill="#ffffff" />
          <TextLines
            lines={label.split('\n')}
            fs={fs}
            box={{ x: m.x - lw / 2 + 6, y: m.y - lh / 2 + 3, w: lw - 12, h: lh - 6 }}
            align="center"
            bold={el.bold}
            color={el.color ?? INK}
            valign="top"
          />
        </g>
      )}
    </g>
  )
}

/** The box a line's words take on the board (for pressing them to slide them along it), or null with no words. */
export function labelBox(el: El, g: LineGeom): { x: number; y: number; w: number; h: number } | null {
  const label = el.text ?? ''
  if (!label) return null
  const m = pointAt(g, el.lt ?? 0.5)
  const fs = el.fs ?? DEFAULT_FS['line']!
  const w = Math.max(...label.split('\n').map((l) => textWidth(l, fs, el.bold))) + 12
  const h = label.split('\n').length * fs * LINE_HEIGHT + 6
  return { x: m.x - w / 2, y: m.y - h / 2, w, h }
}

/**
 * A picture, stretched to the element's box (resizing keeps its proportions); a grey card until its bytes are here.
 * Its bytes are fetched once per element shown and let go with it (ui/useBlobUrl) — moving it never fetches again.
 */
function ImageShape({ el }: { el: El }) {
  const src = el.src
  const { url, failed } = useBlobUrl(src ? () => dynamoAttachmentBytes(src.split('/')[1] ?? '', src) : null, [src])
  return (
    <g transform={`translate(${el.x},${el.y})`}>
      {url ? (
        <image href={url} width={el.w} height={el.h} preserveAspectRatio="none" pointerEvents="none" />
      ) : (
        <>
          <rect width={el.w} height={el.h} fill="#e9ecef" stroke="#ced4da" strokeWidth={1} />
          <text x={el.w / 2} y={el.h / 2 + 5} fontSize={14} fontFamily={BOARD_FONT} fill="#868e96" textAnchor="middle">
            {failed ? 'Picture unavailable' : 'Loading picture…'}
          </text>
        </>
      )}
    </g>
  )
}

/**
 * Everything but lines. `editing`: the text is being typed in the editor laid over it, so it is not drawn twice.
 */
export const ElementShape = memo(function ElementShape({ el, editing }: { el: El; editing?: boolean | undefined }) {
  const t = `translate(${el.x},${el.y})`
  switch (el.t) {
    case 'sticky': {
      return (
        <g transform={t}>
          <rect x={1} y={3} width={el.w} height={el.h} fill="rgba(0,0,0,0.10)" rx={2} />
          <rect width={el.w} height={el.h} fill={el.fill && el.fill !== 'none' ? el.fill : '#fff9b1'} rx={2} />
          {!editing && <NoteText el={el} />}
        </g>
      )
    }
    case 'shape': {
      const kind = el.shape ?? 'rect'
      const sw = el.sw ?? 2
      const stroke = el.stroke ?? INK
      const fill = el.fill ?? '#ffffff'
      const { fs, lines } = layoutText(el)
      return (
        <g transform={t}>
          <path
            d={shapePath(kind, el.w, el.h)}
            fill={fill === 'none' ? 'transparent' : fill}
            stroke={stroke === 'none' ? 'none' : stroke}
            strokeWidth={sw}
            strokeDasharray={el.dash ? `${sw * 4} ${sw * 3}` : undefined}
            strokeLinejoin="round"
          />
          {kind === 'cylinder' && stroke !== 'none' && (
            <path
              d={`M0,${Math.min(el.h * 0.14, 18)} A${el.w / 2},${Math.min(el.h * 0.14, 18)} 0 0 0 ${el.w},${Math.min(el.h * 0.14, 18)}`}
              fill="none"
              stroke={stroke}
              strokeWidth={sw}
            />
          )}
          {!editing && (
            <TextLines lines={lines} fs={fs} box={textBox(el)} align={el.align ?? 'center'} bold={el.bold} color={el.color ?? INK} valign="middle" />
          )}
        </g>
      )
    }
    case 'text': {
      const { fs, lines } = layoutText(el)
      return (
        <g transform={t}>
          {el.fill && el.fill !== 'none' && <rect width={el.w} height={el.h} fill={el.fill} rx={4} />}
          {!editing && (
            <TextLines lines={lines} fs={fs} box={textBox(el)} align={el.align ?? 'left'} bold={el.bold} color={el.color ?? INK} valign="top" />
          )}
        </g>
      )
    }
    case 'frame': {
      const title = el.text || 'Frame'
      return (
        <g transform={t}>
          <rect width={el.w} height={el.h} fill={el.fill && el.fill !== 'none' ? el.fill : '#ffffff'} stroke="#ced4da" strokeWidth={1} />
          {!editing && (
            <text x={0} y={-10} fontSize={14} fontFamily={BOARD_FONT} fill="#495057">
              {title.length > 60 ? `${title.slice(0, 59)}…` : title}
            </text>
          )}
        </g>
      )
    }
    case 'image':
      return <ImageShape el={el} />
    case 'pen': {
      const p = el.pts ?? []
      if (p.length < 2) return null
      let d = `M${p[0]},${p[1]}`
      for (let i = 2; i + 1 < p.length; i += 2) d += ` L${p[i]},${p[i + 1]}`
      return (
        <g transform={t}>
          <path d={d} fill="none" stroke={el.stroke ?? INK} strokeWidth={el.sw ?? 3} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )
    }
    default:
      return null
  }
})

/** A line, looked up against the board (it follows the elements it holds). */
export function LineView({ el, els, faded, editing }: { el: El; els: Record<string, El>; faded?: boolean | undefined; editing?: boolean | undefined }) {
  const g = lineGeom(el, els)
  if (!g) return null
  return <LineShape el={el} g={g} hidden={faded} editing={editing} hops={el.jump ? hopsFor(el, g, linesUnder(el, els)) : undefined} />
}

/** The lines drawn before this one (under it), as drawn — what a jumping line hops over. */
function linesUnder(el: El, els: Record<string, El>): LineGeom[] {
  const out: LineGeom[] = []
  for (const o of paintOrder(els)) {
    if (o.id === el.id) break
    if (o.t !== 'line') continue
    const g = lineGeom(o, els)
    if (g) out.push(g)
  }
  return out
}
